import { MENTION_TOKEN, type User } from '@projektverwaltung/shared';
/**
 * WYSIWYG-Mention-Editor auf TipTap-Basis.
 *
 * Storage-Format bleibt `@[uuid]` im body (kompatibel mit Backend +
 * renderMentionBody). Mentions werden im Editor als atomarer Chip
 * dargestellt; serialize-Pfad konvertiert beim onUpdate zurück nach
 * `@[uuid]`.
 */
import { Mention } from '@tiptap/extension-mention';
import { Placeholder } from '@tiptap/extension-placeholder';
import type { Node } from '@tiptap/pm/model';
import { EditorContent, type JSONContent, ReactRenderer, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import type { SuggestionOptions } from '@tiptap/suggestion';
import {
  type ComponentRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { api } from '../lib/api.js';
import { leseEntwurf, loescheEntwurf, schreibeEntwurf } from '../lib/kommentar-entwurf.js';
import { Avatar } from './avatar.js';

type Props = {
  value: string;
  onChange: (body: string, mentions: string[]) => void;
  placeholder?: string;
  rows?: number;
  /**
   * Opt-in Bild-Einfügen: liefert für eine eingefügte Bilddatei die (browser-ladbare)
   * URL. Ist es gesetzt, fügt Strg+V eines Screenshots das Bild als Markdown-Text
   * `![Screenshot](url)` an der Cursorposition ein. Ohne das Prop kein Bild-Handling
   * (z. B. Staffing-Kommentare bleiben unverändert).
   */
  imageUpload?: (file: File) => Promise<string>;
  /**
   * Opt-in Entwurfserhalt (#137): Unter diesem Schlüssel hält der Editor seinen ungesendeten
   * Inhalt in sessionStorage (`lib/kommentar-entwurf.ts`) und stellt ihn beim nächsten Einbau
   * wieder her — gemeldet über `onChange`, damit Zähler und Senden-Knopf stimmen. Setzt der
   * Aufrufer `value` auf '' (nach dem Senden), ist der Entwurf gelöscht.
   */
  entwurfSchluessel?: string;
  /**
   * #162: vorhandener Text zum Bearbeiten (Speicherformat mit `@[uuid]`). Wird beim Einbau
   * geladen (Vorrang vor einem Entwurf) und über `onChange` gemeldet; `namen` beschriftet die Chips.
   */
  startWert?: { body: string; namen: ReadonlyMap<string, string> };
};

/** Speicherformat (`@[uuid]`, `\n` je Absatz) → TipTap-Dokument mit Mention-Chips. */
export function bodyZuDoc(body: string, namen: ReadonlyMap<string, string>): JSONContent {
  const absaetze = body.split('\n').map((zeile) => {
    const inhalt: JSONContent[] = [];
    let rest = 0;
    for (const m of zeile.matchAll(new RegExp(MENTION_TOKEN.source, 'g'))) {
      const id = m[1] as string;
      if (m.index > rest) inhalt.push({ type: 'text', text: zeile.slice(rest, m.index) });
      inhalt.push({ type: 'mention', attrs: { id, label: namen.get(id) ?? id } });
      rest = m.index + m[0].length;
    }
    if (rest < zeile.length) inhalt.push({ type: 'text', text: zeile.slice(rest) });
    return inhalt.length > 0 ? { type: 'paragraph', content: inhalt } : { type: 'paragraph' };
  });
  return { type: 'doc', content: absaetze };
}

// Token-Format (`@[<uuid>]`) + Extraktion kommen aus @projektverwaltung/shared
// (Single Source of Truth — identisch zur serverseitigen Mention-Ableitung).
export { extractMentions } from '@projektverwaltung/shared';

function serializeDoc(doc: Node): { body: string; mentions: string[] } {
  let body = '';
  const ids: string[] = [];
  doc.descendants((node, _pos, parent) => {
    if (node.type.name === 'mention') {
      const id = node.attrs.id as string | null;
      if (id) {
        body += `@[${id}]`;
        ids.push(id);
      }
      return false;
    }
    if (node.isText) {
      body += node.text ?? '';
      return false;
    }
    if (node.type.name === 'hardBreak') {
      body += '\n';
      return false;
    }
    if (node.type.name === 'paragraph' && parent && parent.type.name === 'doc') {
      // Trennzeichen zwischen Absätzen; vor erstem Absatz keiner.
      if (body.length > 0) body += '\n';
    }
    return true;
  });
  return { body, mentions: [...new Set(ids)] };
}

export function MentionInput({
  value,
  onChange,
  placeholder,
  rows = 3,
  imageUpload,
  entwurfSchluessel,
  startWert,
}: Props) {
  const minHeight = Math.max(48, rows * 22);

  // Ref, damit der (einmal erzeugte) Paste-Handler stets den aktuellen Uploader sieht.
  const imageUploadRef = useRef(imageUpload);
  imageUploadRef.current = imageUpload;
  const entwurfRef = useRef(entwurfSchluessel);
  entwurfRef.current = entwurfSchluessel;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // wir wollen nur Paragraph + Text + Hard-Break — kein Heading, kein List etc.
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      Mention.configure({
        HTMLAttributes: { class: 'mention-chip' },
        renderText({ node }) {
          return `@[${node.attrs.id}]`;
        },
        renderHTML({ node }) {
          return [
            'span',
            { 'data-mention-id': node.attrs.id, class: 'mention-chip' },
            `@${node.attrs.label ?? node.attrs.id}`,
          ];
        },
        suggestion: makeSuggestion(),
      }),
    ],
    content: startWert
      ? bodyZuDoc(startWert.body, startWert.namen)
      : (entwurfSchluessel && leseEntwurf(entwurfSchluessel)) || '',
    // Bearbeiten (#162): Fokus und Cursor ans Ende, man schreibt meist weiter.
    autofocus: startWert ? 'end' : false,
    editorProps: {
      attributes: {
        class: 'mention-editor-content',
        'data-placeholder': placeholder ?? '',
      },
      // Screenshot/Bild aus der Zwischenablage → hochladen und als Markdown-Text
      // `![Screenshot](url)` an der Cursorposition einfügen (Anzeige rendert es als Bild).
      handlePaste: (view, event) => {
        const uploader = imageUploadRef.current;
        if (!uploader) return false;
        const files = Array.from(event.clipboardData?.files ?? []);
        const image = files.find((f) => f.type.startsWith('image/'));
        if (!image) return false;
        event.preventDefault();
        void uploader(image)
          .then((url) => {
            // View kann nach async-Upload zerstört sein (Unmount/Navigation) → absichern.
            try {
              view.dispatch(view.state.tr.insertText(`![Screenshot](${url})`));
              view.focus();
            } catch {
              // View nicht mehr aktiv — nichts einzufügen.
            }
          })
          .catch(() => {
            // Fehler wurde bereits vom Uploader getoastet (handleImageUpload) — hier nur
            // die Rejection schlucken, damit keine Unhandled-Rejection entsteht.
          });
        return true;
      },
    },
    // Wiederhergestellter Entwurf (#137): dem Aufrufer melden, sonst bliebe sein `value` leer.
    onCreate({ editor }) {
      const { body, mentions } = serializeDoc(editor.state.doc);
      if (body !== '') onChange(body, mentions);
    },
    onUpdate({ editor }) {
      const { body, mentions } = serializeDoc(editor.state.doc);
      const schluessel = entwurfRef.current;
      if (schluessel) {
        if (body === '') loescheEntwurf(schluessel);
        else schreibeEntwurf(schluessel, editor.getJSON());
      }
      onChange(body, mentions);
    },
  });

  // Externes `value` zurücksetzen (z.B. nach Submit auf ''). Nur beim Übergang auf '', nicht
  // schon beim Einbau: dort ist `value` noch '', ein wiederhergestellter Entwurf (#137) aber
  // bereits im Editor — er wird erst über `onCreate` gemeldet.
  const vorherigerWert = useRef(value);
  useEffect(() => {
    const vorher = vorherigerWert.current;
    vorherigerWert.current = value;
    if (!editor || value !== '' || vorher === '') return;
    const current = serializeDoc(editor.state.doc).body;
    if (current !== '') {
      editor.commands.clearContent(false);
    }
    if (entwurfRef.current) loescheEntwurf(entwurfRef.current);
  }, [value, editor]);

  return (
    <div className="relative">
      <div
        className="rounded-lg overflow-hidden"
        style={{
          background: 'var(--surface)',
          color: 'var(--ink)',
          border: '1px solid var(--line-soft)',
          minHeight,
        }}
      >
        <EditorContent editor={editor} />
      </div>
      <div className="text-[11px] mt-1" style={{ color: 'var(--ink-3)' }}>
        Tipp:{' '}
        <kbd
          className="px-1 py-0.5 rounded"
          style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
        >
          @
        </kbd>{' '}
        erwähnt Personen
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Suggestion-Plugin (TipTap)
// ---------------------------------------------------------------------------

type SuggestionItem = Pick<User, 'user_id' | 'name' | 'email' | 'm365_photo_url'>;

function makeSuggestion(): Omit<SuggestionOptions<SuggestionItem>, 'editor'> {
  return {
    char: '@',
    items: async ({ query }) => {
      const res = await api.listUsers(query || undefined);
      // #116: Bei leerem Suchbegriff (nur „@") liefert der Server alle Nutzer inkl. Inaktiver.
      return res.items.filter((u) => u.active !== false).slice(0, 8);
    },
    command: ({ editor, range, props }) => {
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: 'mention',
            attrs: { id: props.user_id, label: props.name },
          },
          { type: 'text', text: ' ' },
        ])
        .run();
    },
    render: () => {
      let component: ReactRenderer<MentionListHandle, MentionListProps> | null = null;
      return {
        onStart(props) {
          component = new ReactRenderer(MentionList, {
            props: { items: props.items, command: props.command, clientRect: props.clientRect },
            editor: props.editor,
          });
          // ReactRenderer hängt das DOM-Element nicht selbst ein — ohne tippy.js
          // müssen wir das manuell tun, sonst rendert die Suggestion-Liste nirgends.
          if (component.element) document.body.appendChild(component.element);
        },
        onUpdate(props) {
          component?.updateProps({
            items: props.items,
            command: props.command,
            clientRect: props.clientRect,
          });
        },
        onKeyDown(props) {
          if (props.event.key === 'Escape') {
            component?.element?.remove();
            component?.destroy();
            component = null;
            return true;
          }
          return component?.ref?.onKeyDown(props.event) ?? false;
        },
        onExit() {
          component?.element?.remove();
          component?.destroy();
          component = null;
        },
      };
    },
  };
}

// ---------------------------------------------------------------------------
// Suggestion-Popup
// ---------------------------------------------------------------------------

type MentionListProps = {
  items: SuggestionItem[];
  command: (item: SuggestionItem) => void;
  clientRect?: (() => DOMRect | null) | null;
};

type MentionListHandle = {
  onKeyDown: (e: KeyboardEvent) => boolean;
};

/**
 * Exportiert nur, damit `mention-input-popover.test.tsx` die Positionierung direkt prüfen
 * kann (#68). Der Produktivpfad geht ausschließlich über `render()` der Suggestion-Option.
 */
export const MentionList = forwardRef<MentionListHandle, MentionListProps>(function MentionList(
  { items, command, clientRect },
  ref,
) {
  const [highlight, setHighlight] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => setHighlight(0), [items]);

  useLayoutEffect(() => {
    if (!clientRect) return;
    // #59: KEIN window.scrollY/scrollX aufaddieren. Das Popup wird mit
    // `position: fixed` gerendert, und `clientRect()` liefert bereits
    // viewport-relative Koordinaten — beides zusammen verschob das Popup auf
    // gescrollten Seiten um exakt die Scroll-Distanz nach unten. In der
    // Projektdetail-Ansicht fiel es kaum auf, weil dort selten gescrollt wird;
    // in der Staffing-/KFML-Worklist-Ansicht scrollt das Fenster regelmäßig.
    // Der Scroll-Offset wäre nur bei `position: absolute` im Dokumentfluss richtig.
    //
    // #68: Einmal messen reicht nicht. Scrollt die Seite (in dieser Shell das Dokument, nicht
    // `<main>` — gemessen 2026-09-19, siehe CLAUDE.md) oder ein innerer Container, blieb die
    // Liste stehen, während das Eingabefeld unter ihr wegwanderte. Sie wandert jetzt mit (bewusst statt „schließen": beim Tippen einer
    // Mention scrollt der Browser das Feld selbst gelegentlich nach, und ein Popover, das
    // dabei zuklappt, wäre die schlechtere Antwort).
    const messen = () => {
      const rect = clientRect();
      if (!rect) return;
      setPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 240),
      });
    };
    messen();
    // `capture: true`: Scroll-Ereignisse innerer Container steigen nicht auf, ohne
    // Capture-Phase sähe `window` nur den Dokument-Scroll. So deckt ein Listener beides ab.
    window.addEventListener('scroll', messen, true);
    window.addEventListener('resize', messen);
    return () => {
      window.removeEventListener('scroll', messen, true);
      window.removeEventListener('resize', messen);
    };
  }, [clientRect, items]);

  useImperativeHandle(ref, () => ({
    onKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowDown') {
        setHighlight((h) => Math.min(h + 1, items.length - 1));
        return true;
      }
      if (e.key === 'ArrowUp') {
        setHighlight((h) => Math.max(h - 1, 0));
        return true;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const item = items[highlight];
        if (item) command(item);
        return true;
      }
      return false;
    },
  }));

  if (!pos || items.length === 0) return null;

  // #68: Die transform-Falle aus #67 greift hier NICHT — `onStart` hängt das
  // ReactRenderer-Element (ein loses `div`, das TipTap nirgends selbst einfügt) direkt an
  // `document.body`. Es gibt also gar keinen `<main>`-Vorfahren, gegen den die
  // `fixed`-Koordinaten falsch interpretiert werden könnten. Kein zusätzliches Portal, kein
  // `<Modal>` — ein Popover klebt an einem Element und sperrt nichts.
  return (
    <div
      ref={containerRef}
      className="fixed z-30 rounded-lg max-h-64 overflow-y-auto"
      style={{
        top: pos.top,
        left: pos.left,
        width: pos.width,
        background: 'var(--surface)',
        border: '1px solid var(--line-soft)',
        boxShadow: 'var(--shadow-card-hover)',
      }}
    >
      <ul>
        {items.map((u, i) => (
          <li key={u.user_id}>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                command(u);
              }}
              className="w-full text-left px-3 py-2 flex items-center gap-2"
              style={{
                background: i === highlight ? 'var(--surface-2)' : 'transparent',
                color: 'var(--ink)',
              }}
            >
              <Avatar user={u} size={24} />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate" style={{ color: 'var(--ink)' }}>
                  {u.name}
                </div>
                <div className="text-xs truncate" style={{ color: 'var(--ink-3)' }}>
                  {u.email}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
});

// Tracker für ComponentRef-Resolution; nicht direkt verwendet, hilft nur TS.
export type _MentionListRef = ComponentRef<typeof MentionList>;

// ---------------------------------------------------------------------------
// Read-Only-Renderer (unverändert)
// ---------------------------------------------------------------------------

/**
 * Plain-Text-Variante von `renderMentionBody`: ersetzt die `@[uuid]`-Tokens durch `@Name`.
 *
 * Für einzeilige Vorschauen und `title`-Tooltips, wo React-Nodes nicht gehen (ein Attribut
 * nimmt nur einen String). Liegt bewusst hier neben `renderMentionBody` — beide teilen sich
 * `MENTION_TOKEN` und die Fallback-Regel (unbekannte ID → gekürzte UUID).
 */
export function mentionBodyToPlainText(
  body: string,
  usersById: Map<string, { name: string }>,
): string {
  return body.replace(new RegExp(MENTION_TOKEN.source, 'g'), (_match: string, id?: string) => {
    const uuid = id ?? '';
    return `@${usersById.get(uuid)?.name ?? uuid.slice(0, 8)}`;
  });
}

export function renderMentionBody(body: string, usersById: Map<string, User>): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  const re = new RegExp(MENTION_TOKEN.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) parts.push(body.slice(last, m.index));
    const id = m[1] ?? '';
    const u = usersById.get(id);
    parts.push(
      <span
        key={`m${key++}`}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded text-xs font-medium align-baseline"
        style={{ background: 'var(--primary-soft)', color: 'var(--primary)' }}
        title={u?.email ?? id}
      >
        @{u?.name ?? id.slice(0, 8)}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return parts;
}
